import {
    Component,
    OnInit,
    AfterViewInit,
    ViewChild,
    ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';

// Angular Material imports
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { ControlDusStateService } from '../../services/control-dus-state.service';
import { ControlDusNavigationService } from '../../services/control-dus-navigation.service';

import { LoggerService } from '../../core/services/logger.service';
import { environment } from '../../../environments/environment';
import {
    ControlDusService,
    ControlDusFilter,
    ControlDusItem,
    ComboOption,
    TemporadaDto,
    DispatchGuideReportItem,
} from '../../services/control-dus.service';
import { ControlDusVencimientoChartComponent } from './control-dus-vencimiento-chart/control-dus-vencimiento-chart.component';
import { ControlDusProrrogaChartComponent } from './control-dus-prorroga-chart/control-dus-prorroga-chart.component';
import { ControlDusAtrasoChartComponent } from './control-dus-atraso-chart/control-dus-atraso-chart.component';
import { DocumentosControlDusDialogComponent } from './documentos-control-dus-dialog/documentos-control-dus-dialog.component';
import { UserService, Client } from '../../services/user.service';
import { AuthService, User } from '../../services/auth.service';
import { ClientService } from '../../services/client.service';
import { CommonsService, IVessel } from '../../services/commons.service';
import {
    SkeletonTableComponent,
    SkeletonColumn,
} from '../../shared/components/skeleton-table/skeleton-table.component';
import { catchError, finalize, startWith, map } from 'rxjs/operators';
import { of, Observable, firstValueFrom } from 'rxjs';
import * as ExcelJS from 'exceljs';
import { getDefaultDateRange } from '../../core/utils/date.utils';

@Component({
    selector: 'app-control-dus',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatButtonModule,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatMenuModule,
        MatChipsModule,
        MatTooltipModule,
        MatSelectModule,
        MatAutocompleteModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatDialogModule,
        MatProgressSpinnerModule,
        FormsModule,
        ReactiveFormsModule,
        MatSnackBarModule,
        ControlDusVencimientoChartComponent,
        ControlDusProrrogaChartComponent,
        ControlDusAtrasoChartComponent,
        SkeletonTableComponent,
    ],
    templateUrl: './control-dus.component.html',
    styleUrl: './control-dus.component.scss',
})
export class ControlDusComponent implements OnInit, AfterViewInit {
    // Columnas de la tabla
    displayedColumns: string[] = [
        'refCliente',
        'contenedor',
        'plts',
        'dus',
        'sps',
        'isf',
        'reserva',
        'tipoEmbarque',
        'nave',
        'pol',
        'etd',
        'pod',
        'dias',
        'estado',
        'semaforo',
        'acciones',
    ];

    // Skeleton loading columns config
    skeletonColumns: SkeletonColumn[] = [
        { width: '100px' }, // refCliente
        { width: '110px' }, // contenedor
        { width: '40px' }, // plts
        { width: '100px' }, // dus
        { width: '80px' }, // sps
        { width: '80px' }, // isf
        { width: '110px' }, // reserva
        { width: '90px' }, // tipoEmbarque
        { width: '100px' }, // nave
        { width: '70px' }, // pol
        { width: '80px' }, // etd
        { width: '70px' }, // pod
        { width: '40px' }, // dias
        { width: '80px', pill: true }, // estado
        { width: '50px', pill: true }, // semaforo
        { width: '50px' }, // acciones
    ];

    // Datos de la tabla
    dataSource: ControlDusItem[] = [];
    filteredDataSource: ControlDusItem[] = [];

    // Filtros
    clienteControl = new FormControl<string | number>('');
    clienteSeleccionado: Client | null = null;
    filtroTemporada = new FormControl<string | null>(null);
    filtroNave = new FormControl<string | number>('');
    filtroFechaInicio: Date | null = null;
    filtroFechaFin: Date | null = null;
    filtroRefCliente: string = '';
    filtroContenedor: string = '';
    filtroDus: string = '';
    filtroBooking: string = '';
    filtroEtapa: string | null = null;
    filtroEstado: string | null = null;
    datesLocked = false;

    // Opciones para filtros
    clientes: Client[] = [];
    clientesFiltrados: Observable<Client[]>;
    isLoadingClientes = false;

    naves: IVessel[] = [];
    filteredNavesOptions: IVessel[] = [];
    etapas: ComboOption[] = [];
    estados: ComboOption[] = [];
    temporadasOptions: { value: string; label: string }[] = [];
    temporadasData: TemporadaDto[] = [];

    // Cliente filter for consignatario (populated from new endpoint)
    filtroClienteConsignatario = new FormControl<number | null>(null);
    clientesConsignatario: { clienteId: number; cliente: string }[] = [];

    // Usuario logueado y control de rol cliente
    currentUser: User | null = null;
    isClienteRole = false;
    isConsignatarioRole = false;
    clienteInputDisabled = false;

    // Paginación
    pageSize = 10;
    pageSizeOptions = [10, 25, 50, 100];

    // Estado de carga
    isLoading = false;
    isGeneratingExcel = false;
    isGeneratingReporteGuias = false;
    totalItems = 0;
    currentPage = 1;

    // Ordenamiento
    orderBy: string = 'etd';
    orderDirection: 'ASC' | 'DESC' = 'DESC';

    // Filtro de búsqueda local
    filtroTexto = '';
    searchTerm = '';

    // Stats
    totalDus: number = 0;

    // Estadísticas para gráficos
    vencimientoStats: { rango: string; cantidad: number }[] = [];
    prorrogaStats: { prorroga: string; cantidad: number }[] = [];
    atrasoStats: { tipoAtraso: string; cantidad: number }[] = [];

    // API URL
    private readonly apiUrl = environment.apiUrl;

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    // Métodos auxiliares
    private formatearFechaParaAPI = (fecha: Date): string => {
        const year = fecha.getFullYear();
        const month = String(fecha.getMonth() + 1).padStart(2, '0');
        const day = String(fecha.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    /**
     * Normaliza caracteres con problemas de encoding que vienen de la base de datos
     * Convierte: ? → ó, á, é, í, ú (según el contexto)
     */
    private normalizarEncoding(texto: string | undefined): string {
        if (!texto) return '';

        return texto
            .replace(/Documentaci\?n/gi, 'Documentación')
            .replace(/Legalizaci\?n/gi, 'Legalización')
            .replace(/Operaci\?n/gi, 'Operación')
            .replace(/Revisi\?n/gi, 'Revisión')
            .replace(/Inspecci\?n/gi, 'Inspección')
            .replace(/Tramitaci\?n/gi, 'Tramitación');
    }

    /**
     * Normaliza los datos de un item de Control DUS
     */
    private normalizarItemControlDus(item: ControlDusItem): ControlDusItem {
        return {
            ...item,
            etapa: this.normalizarEncoding(item.etapa),
            cliente: this.normalizarEncoding(item.cliente),
            exportador: this.normalizarEncoding(item.exportador),
            pol: this.normalizarEncoding(item.pol),
            pod: this.normalizarEncoding(item.pod),
            comentarios: this.normalizarEncoding(item.comentarios),
        };
    }

    private _filtrarClientes = (value: string | number | null): Client[] => {
        if (!value) {
            return this.clientes.slice();
        }

        // Si es un número (ID), buscar por ID
        if (typeof value === 'number') {
            const found = this.clientes.find((c) => c.clienteId === value);
            return found ? [found] : this.clientes.slice(0, 50);
        }

        // Si es string, buscar por nombre o ID
        const searchText = String(value).toLowerCase().trim();
        if (searchText === '') {
            return this.clientes.slice(0, 50);
        }

        return this.clientes
            .filter(
                (cliente) =>
                    cliente.cliente.toLowerCase().includes(searchText) ||
                    String(cliente.clienteId).includes(searchText)
            )
            .slice(0, 50);
    };

    mostrarCliente = (value: unknown): string => {
        if (typeof value === 'string') return value;
        if (typeof value === 'number') {
            const found = this.clientes.find((c) => c.clienteId === value);
            return found ? found.cliente : String(value);
        }
        return '';
    };

    /**
     * Actualiza la lista filtrada de naves para el autocompletar (igual que OS Agencia)
     */
    private updateFilteredNaves(input: unknown): void {
        let term = '';
        if (typeof input === 'string') term = input.toLowerCase().trim();
        if (typeof input === 'number') {
            const match = this.naves.find((n) => n.vesselId === input);
            this.filteredNavesOptions = match
                ? [match]
                : this.naves.slice(0, 50);
            return;
        }
        if (term === '') {
            this.filteredNavesOptions = this.naves.slice(0, 50);
            return;
        }
        this.filteredNavesOptions = this.naves
            .filter(
                (n) =>
                    n.vesselName?.toLowerCase().includes(term) ||
                    String(n.vesselId).includes(term)
            )
            .slice(0, 50);
    }

    /**
     * Mostrar nombre de la nave en el input del autocompletar (igual que OS Agencia)
     */
    displayNave = (value: unknown): string => {
        if (typeof value === 'string') return value;
        if (typeof value === 'number') {
            const found = this.naves.find((n) => n.vesselId === value);
            return found ? found.vesselName : String(value);
        }
        return '';
    };

    constructor(
        // eslint-disable-next-line no-unused-vars
        private controlDusService: ControlDusService,
        // eslint-disable-next-line no-unused-vars
        private userService: UserService,
        // eslint-disable-next-line no-unused-vars
        private authService: AuthService,
        // eslint-disable-next-line no-unused-vars
        private clientService: ClientService,
        // eslint-disable-next-line no-unused-vars
        private commonsService: CommonsService,
        // eslint-disable-next-line no-unused-vars
        private snackBar: MatSnackBar,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService,
        // eslint-disable-next-line no-unused-vars
        private router: Router,
        // eslint-disable-next-line no-unused-vars
        private route: ActivatedRoute,
        // eslint-disable-next-line no-unused-vars
        private controlDusState: ControlDusStateService,
        // eslint-disable-next-line no-unused-vars
        private controlDusNavigation: ControlDusNavigationService,
        // eslint-disable-next-line no-unused-vars
        private cdr: ChangeDetectorRef,
        // eslint-disable-next-line no-unused-vars
        private dialog: MatDialog
    ) {
        // Configurar autocompletar de clientes
        this.clientesFiltrados = this.clienteControl.valueChanges.pipe(
            startWith(''),
            map((value) => this._filtrarClientes(value))
        );

        // Configurar autocompletar de naves (igual que OS Agencia)
        this.filtroNave.valueChanges.subscribe((value) => {
            this.updateFilteredNaves(value);
        });
    }

    ngOnInit(): void {
        this.inicializarUsuario();
        this.cargarClientes();
        this.cargarNaves();
        this.cargarEtapas();
        this.cargarEstados();
        this.cargarTemporadas(); // Llama a cargarDatos() internamente después de configurar temporada

        if (this.controlDusNavigation.loadStateFromUrl(this.route)) {
            this.aplicarEstadoDesdeStateService();
        }

        // Fechas por defecto si no hay state previo
        if (!this.filtroFechaInicio && !this.filtroFechaFin) {
            const { from, to } = getDefaultDateRange();
            this.filtroFechaInicio = from;
            this.filtroFechaFin = to;
        }
        // NOTA: cargarDatos() ahora se llama desde cargarTemporadas() para asegurar que el filtro de temporada esté configurado
    }

    ngAfterViewInit(): void {
        this.configurarSortingYPaginacion();
    }

    inicializarUsuario(): void {
        this.currentUser = this.authService.getCurrentUser();

        if (this.currentUser) {
            this.isClienteRole = this.authService.hasRole('client');
            this.isConsignatarioRole = this.authService.hasRole('consignatario');
            this.logger.info(
                `Usuario iniciado - Role: ${
                    this.isClienteRole ? 'cliente' : this.isConsignatarioRole ? 'consignatario' : 'otros'
                }, ClientId: ${this.currentUser?.clientId || 'N/A'}`,
                'ControlDusComponent'
            );

            this.configurarColumnasSegunRol();
            this.configurarFiltroCliente();
        }
    }

    /**
     * Configura el filtro de cliente según el tipo de usuario (igual que OS Agencia)
     */
    configurarFiltroCliente(): void {
        if (this.isClienteRole && this.currentUser?.clientId) {
            // Si es usuario cliente, mostrar el NOMBRE del cliente en el input (como en otras páginas)
            const clientIdNum = Number(this.currentUser.clientId);
            this.userService.getActiveClients().subscribe({
                next: (resp) => {
                    const nombre = resp?.data?.find(
                        (c) => c.clienteId === clientIdNum
                    )?.cliente;
                    this.clienteControl.setValue(
                        nombre || `Cliente #${clientIdNum}`
                    );
                    this.clienteControl.disable();
                    this.clienteInputDisabled = true;
                    this.logger.info(
                        '👤 Usuario cliente detectado, filtro de cliente configurado (muestra nombre) y deshabilitado',
                        'ControlDusComponent'
                    );
                    // Cargar datos inmediatamente para rol cliente
                    this.cargarDatos();
                },
                error: () => {
                    // Fallback a mostrar etiqueta genérica si no se pudo resolver el nombre
                    this.clienteControl.setValue(`Cliente #${clientIdNum}`);
                    this.clienteControl.disable();
                    this.clienteInputDisabled = true;
                    // Cargar datos igualmente usando clientId del token
                    this.cargarDatos();
                },
            });
        } else {
            this.logger.info(
                '👤 Usuario no es cliente, filtro de cliente habilitado',
                'ControlDusComponent'
            );
        }
    }

    configurarColumnasSegunRol(): void {
        const columnasBase = [
            'refCliente',
            'contenedor',
            'plts',
            'dus',
            'sps',
            'isf',
            'reserva',
            'tipoEmbarque',
            'nave',
            'pol',
            'etd',
            'pod',
            'dias',
            'estado',
            'semaforo',
            'acciones',
        ];

        if (this.isClienteRole) {
            this.displayedColumns = columnasBase;
            this.logger.info(
                'Columnas configuradas para usuario cliente (sin columna cliente)',
                'ControlDusComponent'
            );
        } else {
            this.displayedColumns = ['cliente', ...columnasBase];
            this.logger.info(
                'Columnas configuradas para usuario no cliente (con columna cliente)',
                'ControlDusComponent'
            );
        }
    }

    cargarClientes(): void {
        if (this.isClienteRole) {
            return;
        }

        if (this.isConsignatarioRole) {
            this.controlDusService.getClientes()
                .pipe(catchError(() => of([])))
                .subscribe((data) => {
                    this.clientesConsignatario = data;
                });
            return;
        }

        this.logger.info(
            'Cargando clientes desde el backend',
            'ControlDusComponent'
        );
        this.isLoadingClientes = true;

        this.userService
            .getActiveClients()
            .pipe(
                catchError((error) => {
                    this.logger.error(
                        'Error al cargar clientes',
                        error as Error,
                        'ControlDusComponent'
                    );
                    return of({ success: false, data: [] });
                }),
                finalize(() => {
                    this.isLoadingClientes = false;
                })
            )
            .subscribe((response) => {
                if (response.success && response.data) {
                    this.clientes = response.data;
                    this.logger.info(
                        `Clientes cargados: ${this.clientes.length}`,
                        'ControlDusComponent'
                    );
                    const cid =
                        this.controlDusState.getCurrentState().filters
                            .clienteId;
                    if (
                        !this.isClienteRole &&
                        cid != null &&
                        this.clientes.length > 0 &&
                        !this.clienteSeleccionado
                    ) {
                        const c = this.clientes.find(
                            (x) => x.clienteId === cid
                        );
                        if (c) {
                            this.clienteSeleccionado = c;
                            this.clienteControl.setValue(c.clienteId);
                            this.cargarDatos();
                        }
                    }
                }
            });
    }

    cargarNaves(): void {
        this.commonsService
            .getVessels()
            .pipe(
                catchError((error) => {
                    this.logger.error(
                        'Error al cargar naves',
                        error as Error,
                        'ControlDusComponent'
                    );
                    return of([]);
                })
            )
            .subscribe((naves) => {
                this.naves = naves;
                this.updateFilteredNaves(this.filtroNave.value);
                this.logger.info(
                    `Naves cargadas: ${this.naves.length}`,
                    'ControlDusComponent'
                );
                const nave =
                    this.controlDusState.getCurrentState().filters.nave;
                if (nave) {
                    this.filtroNave.setValue(nave);
                }
            });
    }

    cargarEtapas(): void {
        this.logger.info(
            '🔄 Iniciando carga de etapas...',
            'ControlDusComponent'
        );

        this.controlDusService
            .getEtapas()
            .pipe(
                catchError((error) => {
                    this.logger.error(
                        'Error al cargar etapas',
                        error as Error,
                        'ControlDusComponent'
                    );
                    return of({ success: false, data: [] });
                })
            )
            .subscribe((response) => {
                this.logger.info(
                    `📦 Respuesta de etapas recibida: ${JSON.stringify(
                        response
                    )}`,
                    'ControlDusComponent'
                );

                if (response.success && response.data) {
                    this.etapas = response.data;
                    this.logger.info(
                        `✅ Etapas cargadas: ${
                            this.etapas.length
                        } - ${JSON.stringify(this.etapas)}`,
                        'ControlDusComponent'
                    );
                    // Forzar detección de cambios para actualizar la vista
                    this.cdr.detectChanges();
                } else {
                    this.logger.warn(
                        `⚠️ Respuesta de etapas sin datos o sin éxito: ${JSON.stringify(
                            response
                        )}`,
                        'ControlDusComponent'
                    );
                }
            });
    }

    cargarEstados(): void {
        this.logger.info(
            '🔄 Iniciando carga de estados...',
            'ControlDusComponent'
        );

        this.controlDusService
            .getEstados()
            .pipe(
                catchError((error) => {
                    this.logger.error(
                        'Error al cargar estados',
                        error as Error,
                        'ControlDusComponent'
                    );
                    return of({ success: false, data: [] });
                })
            )
            .subscribe((response) => {
                this.logger.info(
                    `📦 Respuesta de estados recibida: ${JSON.stringify(
                        response
                    )}`,
                    'ControlDusComponent'
                );

                if (response.success && response.data) {
                    this.estados = response.data;
                    this.logger.info(
                        `✅ Estados cargados: ${
                            this.estados.length
                        } - ${JSON.stringify(this.estados)}`,
                        'ControlDusComponent'
                    );
                    // Forzar detección de cambios para actualizar la vista
                    this.cdr.detectChanges();
                } else {
                    this.logger.warn(
                        `⚠️ Respuesta de estados sin datos o sin éxito: ${JSON.stringify(
                            response
                        )}`,
                        'ControlDusComponent'
                    );
                }
            });
    }

    cargarTemporadas(): void {
        this.logger.info(
            '🔄 Iniciando carga de temporadas...',
            'ControlDusComponent'
        );

        this.controlDusService
            .getTemporadas()
            .pipe(
                catchError((error) => {
                    this.logger.error(
                        'Error al cargar temporadas',
                        error as Error,
                        'ControlDusComponent'
                    );
                    return of({ data: [], total: 0, timestamp: '' });
                })
            )
            .subscribe((response) => {
                if (response?.data) {
                    // Ordenar por seasonId descendente (más recientes primero)
                    const temporadasOrdenadas = response.data
                        .slice()
                        .sort((a, b) => (b.seasonId || 0) - (a.seasonId || 0));

                    this.temporadasOptions = temporadasOrdenadas.map((t) => ({
                        value: t.seasonId.toString(),
                        label: t.seasonName || `${t.seasonId}`,
                    }));

                    this.temporadasData = temporadasOrdenadas;

                    this.logger.info(
                        `✅ Temporadas cargadas: ${this.temporadasOptions.length}`,
                        'ControlDusComponent'
                    );

                    // Aplicar temporada desde el estado o seleccionar la última por defecto
                    const savedTemporada =
                        this.controlDusState.getCurrentState().filters
                            .temporadaId;
                    if (savedTemporada) {
                        this.filtroTemporada.setValue(
                            savedTemporada.toString()
                        );
                    } else if (temporadasOrdenadas.length > 0) {
                        // Seleccionar la última temporada (primera del array ordenado DESC)
                        const ultimaTemporada = temporadasOrdenadas[0];
                        this.filtroTemporada.setValue(
                            ultimaTemporada.seasonId.toString()
                        );
                        this.logger.info(
                            `📅 Temporada por defecto: ${ultimaTemporada.seasonName}`,
                            'ControlDusComponent'
                        );
                    }

                    this.cdr.detectChanges();

                    // Cargar datos después de configurar temporada (para usuarios no-cliente)
                    // Los usuarios cliente cargan datos desde configurarFiltroCliente()
                    if (!this.isClienteRole) {
                        this.cargarDatos();
                    }
                } else {
                    this.logger.warn(
                        '⚠️ No se recibieron datos de temporadas',
                        'ControlDusComponent'
                    );
                    this.temporadasOptions = [];
                    this.temporadasData = [];

                    // Aún así cargar datos para usuarios no-cliente
                    if (!this.isClienteRole) {
                        this.cargarDatos();
                    }
                }
            });
    }

    cargarDatos(): void {
        this.logger.info(
            '🚀 Iniciando carga de datos de Control DUS (paralela)',
            'ControlDusComponent'
        );

        const fechaDesde: string | undefined = this.filtroFechaInicio
            ? this.formatearFechaParaAPI(this.filtroFechaInicio)
            : undefined;
        const fechaHasta: string | undefined = this.filtroFechaFin
            ? this.formatearFechaParaAPI(this.filtroFechaFin)
            : undefined;

        // Obtener clienteId del FormControl (igual que OS Agencia)
        let clienteId: number | undefined = undefined;
        if (this.isClienteRole && this.currentUser?.clientId) {
            // Para rol cliente, forzar el clientId del token sin depender del valor visible del input
            const forced = Number(this.currentUser.clientId);
            clienteId = isNaN(forced) ? undefined : forced;
        } else if (this.isConsignatarioRole) {
            // Para consignatario, usar el combo filtrado por consigneeId
            const v = this.filtroClienteConsignatario.value;
            clienteId = v != null ? v : undefined;
        } else {
            const clienteValue = this.clienteControl.value;
            if (
                clienteValue !== null &&
                clienteValue !== undefined &&
                clienteValue !== ''
            ) {
                const raw = clienteValue as unknown;
                const parsedId =
                    typeof raw === 'number' ? raw : parseInt(String(raw));
                if (!isNaN(parsedId)) {
                    clienteId = parsedId;
                }
            }
        }

        // Obtener vesselId del FormControl de nave (igual que OS Agencia)
        let naveNombre: string | undefined = undefined;
        const naveValue = this.filtroNave.value;
        if (naveValue) {
            if (typeof naveValue === 'number') {
                const nave = this.naves.find((n) => n.vesselId === naveValue);
                naveNombre = nave?.vesselName;
            } else if (typeof naveValue === 'string') {
                naveNombre = naveValue;
            }
        }

        // Obtener temporadaId del FormControl
        let temporadaId: number | undefined = undefined;
        if (this.filtroTemporada.value) {
            const parsedTemp = parseInt(this.filtroTemporada.value, 10);
            if (!isNaN(parsedTemp)) {
                temporadaId = parsedTemp;
            }
        }

        // Filtros base compartidos por ambas llamadas
        const baseFilters: ControlDusFilter = {
            clienteId: clienteId,
            temporadaId: temporadaId,
            nave: naveNombre ?? undefined,
            dateFrom: fechaDesde,
            dateTo: fechaHasta,
            referenciaCliente: this.filtroRefCliente || undefined,
            contenedor: this.filtroContenedor || undefined,
            dus: this.filtroDus || undefined,
            booking: this.filtroBooking || undefined,
            etapa: this.filtroEtapa ?? undefined,
            estado: this.filtroEstado ?? undefined,
            excludeCancelado: true,
        };

        // Filtros para el listado (incluye paginación y ordenamiento)
        const listFilters: ControlDusFilter = {
            ...baseFilters,
            page: this.currentPage,
            limit: this.pageSize,
            orderBy: this.orderBy,
            orderDirection: this.orderDirection,
        };

        this.isLoading = true;

        // ===== LLAMADA 1: Listado (tabla) - se renderiza apenas llegue =====
        this.controlDusService
            .getControlDusList(listFilters)
            .pipe(
                catchError((error) => {
                    this.logger.error(
                        'Error al cargar Control DUS',
                        error as Error,
                        'ControlDusComponent'
                    );
                    this.snackBar.open(
                        'Error al cargar Control DUS',
                        'Cerrar',
                        {
                            duration: 5000,
                            panelClass: ['error-snackbar'],
                        }
                    );
                    return of({
                        data: [],
                        pagination: {
                            total: 0,
                            page: 1,
                            limit: 10,
                            totalPages: 0,
                        },
                        timestamp: new Date().toISOString(),
                    });
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe((response) => {
                // Normalizar encoding de los datos (Cancelado se excluye en backend)
                this.dataSource = (response.data || []).map((item) =>
                    this.normalizarItemControlDus(item)
                );
                this.totalItems = response.pagination.total || 0;
                this.totalDus = this.totalItems;
                this.filtrarDatos();
                this.configurarSortingYPaginacion();
                this.cdr.detectChanges();

                this.logger.info(
                    `✅ Tabla cargada: ${this.dataSource.length} items, total: ${this.totalItems}`,
                    'ControlDusComponent'
                );
            });

        // ===== LLAMADA 2: Estadísticas (gráficos) - se renderiza apenas llegue =====
        this.controlDusService.getStatistics(baseFilters).subscribe({
            next: (stats) => {
                this.vencimientoStats = stats.vencimiento;
                this.prorrogaStats = stats.prorroga;
                this.atrasoStats = stats.atraso;
                this.cdr.detectChanges();

                this.logger.info(
                    `✅ Gráficos cargados: ${stats.vencimiento.length} rangos, ${stats.prorroga.length} prórrogas, ${stats.atraso.length} tipos de atraso`,
                    'ControlDusComponent'
                );
            },
            error: (error) => {
                this.logger.error(
                    'Error al cargar estadísticas para gráficos',
                    error as Error,
                    'ControlDusComponent'
                );
            },
        });
    }

    filtrarDatos(): void {
        if (!this.filtroTexto || this.filtroTexto.trim() === '') {
            this.filteredDataSource = [...this.dataSource];
        } else {
            const termino = this.filtroTexto.toLowerCase().trim();

            this.filteredDataSource = this.dataSource.filter((item) => {
                return (
                    item.cliente?.toLowerCase().includes(termino) ||
                    item.refCliente?.toLowerCase().includes(termino) ||
                    item.contenedor?.toLowerCase().includes(termino) ||
                    item.dus?.toLowerCase().includes(termino) ||
                    item.reserva?.toLowerCase().includes(termino) ||
                    item.nave?.toLowerCase().includes(termino) ||
                    item.etapa?.toLowerCase().includes(termino) ||
                    item.estado.toLowerCase().includes(termino)
                );
            });
        }
    }

    onClienteSeleccionado(cliente: Client): void {
        this.clienteSeleccionado = cliente;
        this.clienteControl.setValue(cliente.clienteId);
        this.logger.info(
            `Cliente seleccionado: ${cliente.cliente} (ID: ${cliente.clienteId})`,
            'ControlDusComponent'
        );
    }

    limpiarCliente(): void {
        if (this.isClienteRole) {
            return;
        }

        this.clienteControl.setValue('');
        this.clienteSeleccionado = null;
        this.cdr.detectChanges();
    }

    actualizarBloqueoFechas(): void {
        const hasValue = !!(
            this.filtroContenedor?.trim() ||
            this.filtroDus?.trim() ||
            this.filtroBooking?.trim()
        );
        if (hasValue) {
            this.filtroFechaInicio = null;
            this.filtroFechaFin = null;
            this.datesLocked = true;
        } else {
            this.datesLocked = false;
        }
    }

    aplicarFiltros(): void {
        this.currentPage = 1;
        if (this.paginator) {
            this.paginator.pageIndex = 0;
        }
        this.actualizarEstadoFiltros();
        this.cargarDatos();
    }

    limpiarFiltros(): void {
        // Solo limpiar el filtro de cliente si NO es usuario cliente (igual que OS Agencia)
        if (!this.isClienteRole) {
            this.clienteControl.setValue('');
            this.clienteSeleccionado = null;
        }
        if (this.isConsignatarioRole) {
            this.filtroClienteConsignatario.setValue(null);
        }

        // Limpiar el resto de los filtros
        this.filtroTemporada.setValue(null);
        this.filtroNave.setValue('');
        this.filtroFechaInicio = null;
        this.filtroFechaFin = null;
        this.filtroRefCliente = '';
        this.filtroContenedor = '';
        this.filtroDus = '';
        this.filtroBooking = '';
        this.filtroEtapa = null;
        this.filtroEstado = null;
        this.filtroTexto = '';
        this.searchTerm = '';
        this.datesLocked = false;

        this.currentPage = 1;
        this.actualizarEstadoFiltros();
        this.cargarDatos();
    }

    onPageChange(event: { pageIndex: number; pageSize: number }): void {
        this.currentPage = event.pageIndex + 1;
        this.pageSize = event.pageSize;
        this.actualizarEstadoFiltros();
        this.cargarDatos();
    }

    onSortChange(event: { active: string; direction: string }): void {
        if (!event.direction) {
            this.orderBy = 'etd';
            this.orderDirection = 'DESC';
        } else {
            this.orderBy = event.active;
            this.orderDirection =
                event.direction.toUpperCase() === 'ASC' ? 'ASC' : 'DESC';
        }

        this.currentPage = 1;
        if (this.paginator) {
            this.paginator.firstPage();
        }
        this.actualizarEstadoFiltros();
        this.cargarDatos();
    }

    configurarSortingYPaginacion(): void {
        if (!this.sort) {
            return;
        }

        this.sort.sortChange.subscribe(({ active, direction }) => {
            this.orderBy = active;
            this.orderDirection =
                direction && direction.toUpperCase() === 'ASC' ? 'ASC' : 'DESC';

            this.currentPage = 1;
            if (this.paginator) {
                this.paginator.firstPage();
            }
            this.cargarDatos();
        });
    }

    verDetalle(dus: ControlDusItem): void {
        this.logger.info(
            `Abriendo detalle de DUS en nueva pestaña: nroIE=${dus.nroIE}, DUS=${dus.dus}`,
            'ControlDusComponent'
        );
        this.actualizarEstadoFiltros();
        this.controlDusNavigation.openDetailInNewTab(dus.nroIE);
    }

    /**
     * Abre el diálogo de documentos para descargar archivos asociados al DUS
     */
    descargarDocumento(dus: ControlDusItem): void {
        this.logger.info(
            `📄 Abrir diálogo de documentos para DUS nroIE: ${dus.nroIE}`
        );

        const dialogRef = this.dialog.open(
            DocumentosControlDusDialogComponent,
            {
                data: {
                    nroIE: dus.nroIE,
                    numeroNroIE: dus.nroIE.toString().padStart(9, '0'),
                    dus: dus.dus,
                    contenedor: dus.contenedor,
                },
                width: '90%',
                maxWidth: '1200px',
                panelClass: 'documentos-dialog',
            }
        );

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.logger.info(
                    'Diálogo de documentos cerrado con resultado:',
                    result
                );
            }
        });
    }

    formatearFecha(fecha: Date | undefined): string {
        if (!fecha) return '';
        return new Date(fecha).toLocaleDateString('es-CL', {
            timeZone: 'UTC',
        });
    }

    formatearNumero(valor: number | undefined): string {
        if (valor === null || valor === undefined) return '0';
        return valor.toLocaleString('es-CL');
    }

    /**
     * Calcula los días transcurridos desde ETD.
     * Si el estado es 'Finalizado', retorna el valor de dias del backend.
     * Si no, calcula los días hasta hoy.
     */
    calcularDias(element: ControlDusItem): number | string {
        if (!element.etd) return '-';

        // Si está finalizado, usar el valor guardado del backend o '-'
        if (element.estado === 'Finalizado') {
            return element.dias ?? '-';
        }

        // Calcular días desde ETD hasta hoy
        const etd = new Date(element.etd);
        const today = new Date();
        const diffMs = today.getTime() - etd.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        return diffDays >= 0 ? diffDays : '-';
    }

    /**
     * Exporta a Excel los registros que cumplen con el filtro actual, sin paginación.
     * Formato con logo y colores OPB, igual que los demás módulos.
     */
    async exportarExcel(): Promise<void> {
        if (this.totalItems === 0) {
            this.snackBar.open('No hay datos para exportar', 'Cerrar', {
                duration: 3000,
                panelClass: ['warning-snackbar'],
            });
            return;
        }

        this.isGeneratingExcel = true;
        this.logger.info(
            '📊 Exportando Excel de Control DUS (filtro actual, sin paginación)',
            'ControlDusComponent'
        );

        try {
            const filters = this.buildFiltrosParaExport();
            const response = await firstValueFrom(
                this.controlDusService.getControlDusList(filters)
            );

            if (!response?.data?.length) {
                this.snackBar.open('No hay datos para exportar', 'Cerrar', {
                    duration: 3000,
                    panelClass: ['warning-snackbar'],
                });
                return;
            }

            const dataNormalizada = (response.data || []).map((item) =>
                this.normalizarItemControlDus(item)
            );
            await this.generarExcelConEstilos(dataNormalizada);

            this.snackBar.open('Excel exportado correctamente', 'Cerrar', {
                duration: 3000,
                panelClass: ['success-snackbar'],
            });
        } catch (error) {
            this.logger.error(
                'Error al exportar Excel de Control DUS',
                error as Error
            );
            this.snackBar.open('Error al exportar Excel', 'Cerrar', {
                duration: 4000,
                panelClass: ['error-snackbar'],
            });
        } finally {
            this.isGeneratingExcel = false;
        }
    }

    /**
     * Construye los mismos filtros que cargarDatos, con page=1 y limit=50000 para traer todos los registros.
     */
    private buildFiltrosParaExport(): ControlDusFilter {
        const fechaDesde: string | undefined = this.filtroFechaInicio
            ? this.formatearFechaParaAPI(this.filtroFechaInicio)
            : undefined;
        const fechaHasta: string | undefined = this.filtroFechaFin
            ? this.formatearFechaParaAPI(this.filtroFechaFin)
            : undefined;

        let clienteId: number | undefined = undefined;
        if (this.isClienteRole && this.currentUser?.clientId) {
            const forced = Number(this.currentUser.clientId);
            clienteId = isNaN(forced) ? undefined : forced;
        } else if (this.isConsignatarioRole) {
            const v = this.filtroClienteConsignatario.value;
            clienteId = v != null ? v : undefined;
        } else {
            const clienteValue = this.clienteControl.value;
            if (
                clienteValue !== null &&
                clienteValue !== undefined &&
                clienteValue !== ''
            ) {
                const raw = clienteValue as unknown;
                const parsedId =
                    typeof raw === 'number' ? raw : parseInt(String(raw), 10);
                if (!isNaN(parsedId)) {
                    clienteId = parsedId;
                }
            }
        }

        let naveNombre: string | undefined = undefined;
        const naveValue = this.filtroNave.value;
        if (naveValue) {
            if (typeof naveValue === 'number') {
                const nave = this.naves.find((n) => n.vesselId === naveValue);
                naveNombre = nave?.vesselName;
            } else if (typeof naveValue === 'string') {
                naveNombre = naveValue;
            }
        }

        // Obtener temporadaId
        let temporadaId: number | undefined = undefined;
        if (this.filtroTemporada.value) {
            const parsedTemp = parseInt(this.filtroTemporada.value, 10);
            if (!isNaN(parsedTemp)) {
                temporadaId = parsedTemp;
            }
        }

        return {
            page: 1,
            limit: 50000,
            orderBy: this.orderBy,
            orderDirection: this.orderDirection,
            clienteId,
            temporadaId,
            nave: naveNombre ?? undefined,
            dateFrom: fechaDesde,
            dateTo: fechaHasta,
            referenciaCliente: this.filtroRefCliente || undefined,
            contenedor: this.filtroContenedor || undefined,
            dus: this.filtroDus || undefined,
            booking: this.filtroBooking || undefined,
            etapa: this.filtroEtapa ?? undefined,
            estado: this.filtroEstado ?? undefined,
        };
    }

    /**
     * Genera el archivo Excel con estilo OPB (logo y colores), igual que órdenes-transporte y otros módulos.
     */
    private async generarExcelConEstilos(
        data: ControlDusItem[]
    ): Promise<void> {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Control DUS');

        const colors = {
            primary: '1e3a8a',
            secondary: '3b82f6',
            dark: '1e293b',
            gray: '64748b',
            light: 'f1f5f9',
            white: 'ffffff',
        };

        let logoArrayBuffer: ArrayBuffer | null = null;
        try {
            const baseHref =
                document.querySelector('base')?.getAttribute('href') || '/';
            const logoPath = `${baseHref}assets/images/logos/logotipo-fondo-claro.png`;
            const logoResponse = await fetch(logoPath);
            if (logoResponse.ok) {
                logoArrayBuffer = await logoResponse.arrayBuffer();
            }
        } catch {
            this.logger.warn(
                'No se pudo cargar el logo para Excel',
                'ControlDusComponent'
            );
        }

        if (logoArrayBuffer) {
            try {
                const logoId = workbook.addImage({
                    buffer: logoArrayBuffer,
                    extension: 'png',
                });
                worksheet.addImage(logoId, {
                    tl: { col: 0.2, row: 0.2 },
                    ext: { width: 150, height: 50 },
                });
            } catch {
                this.logger.warn(
                    'Error al agregar logo al Excel',
                    'ControlDusComponent'
                );
            }
        }

        const titleCell = worksheet.getCell('D1');
        titleCell.value = 'CONTROL DUS';
        titleCell.font = {
            name: 'Calibri',
            size: 20,
            bold: true,
            color: { argb: 'FF' + colors.primary },
        };
        titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
        worksheet.mergeCells('D1:O1');
        worksheet.getRow(1).height = 40;

        const dateCell = worksheet.getCell('D2');
        dateCell.value = `Generado el: ${new Date().toLocaleDateString(
            'es-CL',
            {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }
        )}`;
        dateCell.font = {
            name: 'Calibri',
            size: 11,
            color: { argb: 'FF' + colors.gray },
        };
        dateCell.alignment = { vertical: 'middle', horizontal: 'left' };
        worksheet.mergeCells('D2:O2');
        worksheet.getRow(2).height = 25;

        worksheet.getRow(3).height = 10;

        const columnas = [
            { header: 'Cliente', key: 'cliente', width: 16 },
            { header: 'Nro IE', key: 'nroIE', width: 10 },
            { header: 'Ref. Cliente', key: 'refCliente', width: 12 },
            { header: 'Contenedor', key: 'contenedor', width: 14 },
            { header: 'PLTS', key: 'plts', width: 8 },
            { header: 'DUS', key: 'dus', width: 14 },
            { header: 'SPS', key: 'sps', width: 8 },
            { header: 'ISF', key: 'isf', width: 12 },
            { header: 'Booking', key: 'reserva', width: 16 },
            { header: 'Tipo Embarque', key: 'tipoEmbarque', width: 14 },
            { header: 'Nave', key: 'nave', width: 22 },
            { header: 'ETD', key: 'etd', width: 12 },
            { header: 'Etapa', key: 'etapa', width: 14 },
            { header: 'Estado', key: 'estado', width: 12 },
            { header: 'Comentarios', key: 'comentarios', width: 30 },
        ];

        columnas.forEach((col, i) => {
            worksheet.getColumn(i + 1).width = col.width;
        });

        const headerRow = worksheet.getRow(4);
        headerRow.height = 30;
        columnas.forEach((col, i) => {
            const cell = worksheet.getCell(4, i + 1);
            cell.value = col.header;
            cell.font = {
                name: 'Calibri',
                size: 12,
                bold: true,
                color: { argb: 'FFFFFFFF' },
            };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF' + colors.primary },
            };
            cell.alignment = {
                vertical: 'middle',
                horizontal: 'center',
                wrapText: true,
            };
            cell.border = {
                top: { style: 'medium', color: { argb: 'FF' + colors.dark } },
                bottom: {
                    style: 'medium',
                    color: { argb: 'FF' + colors.dark },
                },
                left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
            };
        });

        const fmtFecha = (f: Date | string | undefined): string =>
            f
                ? new Date(f).toLocaleDateString('es-CL', { timeZone: 'UTC' })
                : '-';

        data.forEach((item, index) => {
            const rowNum = 5 + index;
            const row = worksheet.getRow(rowNum);
            row.height = 22;

            const fillColor =
                index % 2 === 0
                    ? { argb: 'FFFFFFFF' }
                    : { argb: 'FF' + colors.light };

            const values = [
                item.cliente ?? '-',
                item.nroIE ?? '-',
                item.refCliente ?? '-',
                item.contenedor ?? '-',
                item.plts ?? '-',
                item.dus ?? '-',
                item.sps ?? '-',
                fmtFecha(item.isf),
                item.reserva ?? '-',
                item.tipoEmbarque ?? '-',
                item.nave ?? '-',
                fmtFecha(item.etd),
                item.etapa ?? '-',
                item.estado ?? '-',
                item.comentarios ?? '-',
            ];

            values.forEach((val, colIdx) => {
                const cell = row.getCell(colIdx + 1);
                cell.value = val;
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: fillColor,
                };
                cell.font = {
                    name: 'Calibri',
                    size: 11,
                    color: { argb: 'FF000000' },
                };
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: typeof val === 'number' ? 'right' : 'left',
                };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    bottom: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    left: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    right: { style: 'thin', color: { argb: 'FFd1d5db' } },
                };
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `control_dus_${
            new Date().toISOString().split('T')[0]
        }.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    // Las estadísticas ahora se cargan en paralelo directamente en cargarDatos()

    private actualizarEstadoFiltros(): void {
        let clienteId: number | null = null;
        if (this.isClienteRole && this.currentUser?.clientId) {
            clienteId = Number(this.currentUser.clientId);
        } else {
            const v = this.clienteControl.value;
            const id =
                typeof v === 'number'
                    ? v
                    : this.clienteSeleccionado?.clienteId ??
                      (parseInt(String(v || ''), 10) || undefined);
            clienteId = id != null ? id : null;
        }
        let nave: string | null = null;
        const nv = this.filtroNave.value;
        if (nv) {
            if (typeof nv === 'number') {
                const v = this.naves.find((n) => n.vesselId === nv);
                nave = v?.vesselName ?? null;
            } else {
                nave = String(nv);
            }
        }
        // Obtener temporadaId del filtro
        let temporadaId: number | null = null;
        if (this.filtroTemporada.value) {
            const parsed = parseInt(this.filtroTemporada.value, 10);
            if (!isNaN(parsed)) {
                temporadaId = parsed;
            }
        }

        this.controlDusState.updateState({
            filters: {
                clienteId,
                temporadaId,
                nave,
                dateFrom: this.filtroFechaInicio ?? null,
                dateTo: this.filtroFechaFin ?? null,
                referenciaCliente: this.filtroRefCliente || '',
                contenedor: this.filtroContenedor || '',
                dus: this.filtroDus || '',
                booking: this.filtroBooking || '',
                etapa: this.filtroEtapa ?? null,
                estado: this.filtroEstado ?? null,
            },
            pagination: { page: this.currentPage, limit: this.pageSize },
            orderBy: this.orderBy,
            orderDirection: this.orderDirection,
        });
    }

    private aplicarEstadoDesdeStateService(): void {
        const s = this.controlDusState.getCurrentState();
        this.filtroFechaInicio = s.filters.dateFrom ?? null;
        this.filtroFechaFin = s.filters.dateTo ?? null;
        this.filtroRefCliente = s.filters.referenciaCliente || '';
        this.filtroContenedor = s.filters.contenedor || '';
        this.filtroDus = s.filters.dus || '';
        this.filtroBooking = s.filters.booking || '';
        this.filtroEtapa = s.filters.etapa ?? null;
        this.filtroEstado = s.filters.estado ?? null;
        this.currentPage = s.pagination.page;
        this.pageSize = s.pagination.limit;
        this.orderBy = s.orderBy;
        this.orderDirection = s.orderDirection;
        if (this.paginator) {
            this.paginator.pageIndex = this.currentPage - 1;
            this.paginator.pageSize = this.pageSize;
        }
        if (s.filters.temporadaId != null) {
            this.filtroTemporada.setValue(s.filters.temporadaId.toString());
        }
        if (s.filters.nave) {
            this.filtroNave.setValue(s.filters.nave);
        }
        this.actualizarBloqueoFechas();
        if (
            !this.isClienteRole &&
            s.filters.clienteId != null &&
            this.clientes.length > 0
        ) {
            const c = this.clientes.find(
                (x) => x.clienteId === s.filters.clienteId
            );
            if (c) {
                this.clienteSeleccionado = c;
                this.clienteControl.setValue(c.clienteId);
            }
        }
    }

    /**
     * Genera y descarga el Reporte de Guías de Despacho con los filtros activos.
     */
    async exportarReporteGuias(): Promise<void> {
        if (this.totalItems === 0) {
            this.snackBar.open('No hay datos para exportar', 'Cerrar', {
                duration: 3000,
                panelClass: ['warning-snackbar'],
            });
            return;
        }

        this.isGeneratingReporteGuias = true;
        this.logger.info(
            '📋 Exportando Reporte de Guías de Despacho',
            'ControlDusComponent'
        );

        try {
            const filters = this.buildFiltrosParaExport();
            const data = await firstValueFrom(
                this.controlDusService.getDispatcherReport(filters)
            );

            if (!data?.length) {
                this.snackBar.open(
                    'No hay guías de despacho para los filtros actuales',
                    'Cerrar',
                    {
                        duration: 3000,
                        panelClass: ['warning-snackbar'],
                    }
                );
                return;
            }

            await this.generarExcelReporteGuias(data);
            this.snackBar.open('Reporte generado correctamente', 'Cerrar', {
                duration: 3000,
                panelClass: ['success-snackbar'],
            });
        } catch (error) {
            this.logger.error(
                'Error al exportar Reporte de Guías',
                error as Error
            );
            this.snackBar.open('Error al generar el reporte', 'Cerrar', {
                duration: 4000,
                panelClass: ['error-snackbar'],
            });
        } finally {
            this.isGeneratingReporteGuias = false;
        }
    }

    private async generarExcelReporteGuias(
        data: DispatchGuideReportItem[]
    ): Promise<void> {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Guías de Despacho');

        const colors = {
            primary: '1e3a8a',
            dark: '1e293b',
            gray: '64748b',
            light: 'f1f5f9',
        };

        try {
            const baseHref =
                document.querySelector('base')?.getAttribute('href') || '/';
            const logoResponse = await fetch(
                `${baseHref}assets/images/logos/logotipo-fondo-claro.png`
            );
            if (logoResponse.ok) {
                const logoBuffer = await logoResponse.arrayBuffer();
                const logoId = workbook.addImage({
                    buffer: logoBuffer,
                    extension: 'png',
                });
                worksheet.addImage(logoId, {
                    tl: { col: 0.2, row: 0.2 },
                    ext: { width: 150, height: 50 },
                });
            }
        } catch {
            /* logo opcional */
        }

        // Row 1: Título
        const titleCell = worksheet.getCell('D1');
        titleCell.value = 'REPORTE GUÍAS DE DESPACHO';
        titleCell.font = {
            name: 'Calibri',
            size: 18,
            bold: true,
            color: { argb: 'FF' + colors.primary },
        };
        titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
        worksheet.mergeCells('D1:AB1');
        worksheet.getRow(1).height = 40;

        // Row 2: Fecha
        const dateCell = worksheet.getCell('D2');
        dateCell.value = `Generado el: ${new Date().toLocaleDateString(
            'es-CL',
            {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }
        )}`;
        dateCell.font = {
            name: 'Calibri',
            size: 11,
            color: { argb: 'FF' + colors.gray },
        };
        dateCell.alignment = { vertical: 'middle', horizontal: 'left' };
        worksheet.mergeCells('D2:AB2');
        worksheet.getRow(2).height = 25;

        // Row 3: Separador
        worksheet.getRow(3).height = 10;

        const columnas = [
            {
                header: 'Referencia',
                key: 'referencia',
                width: 16,
                numeric: false,
            },
            { header: 'Cliente', key: 'cliente', width: 22, numeric: false },
            {
                header: 'Exportador',
                key: 'exportador',
                width: 20,
                numeric: false,
            },
            { header: 'Guía', key: 'guia', width: 18, numeric: false },
            {
                header: 'Estado Guía',
                key: 'estadoGuia',
                width: 16,
                numeric: false,
            },
            { header: 'Bodega', key: 'bodega', width: 20, numeric: false },
            { header: 'Nivel', key: 'nivel', width: 10, numeric: false },
            { header: 'Patente', key: 'patente', width: 14, numeric: false },
            { header: 'Nave', key: 'nave', width: 22, numeric: false },
            { header: 'Booking', key: 'booking', width: 16, numeric: false },
            { header: 'POL', key: 'pol', width: 10, numeric: false },
            { header: 'ETD', key: 'etd', width: 12, numeric: false },
            { header: 'POD', key: 'pod', width: 10, numeric: false },
            {
                header: 'Consignatario',
                key: 'consignatario',
                width: 22,
                numeric: false,
            },
            { header: 'DUS', key: 'dus', width: 16, numeric: false },
            {
                header: 'Contenedor',
                key: 'contenedor',
                width: 16,
                numeric: false,
            },
            {
                header: 'Cant. Cntrs',
                key: 'cantidadCntrs',
                width: 12,
                numeric: true,
            },
            {
                header: 'Tipo Embarque',
                key: 'tipoEmbarque',
                width: 14,
                numeric: false,
            },
            {
                header: 'Cant. Pallets',
                key: 'cantidadPallets',
                width: 13,
                numeric: true,
            },
            { header: 'Despacho', key: 'despacho', width: 14, numeric: false },
            { header: 'Visación', key: 'visacion', width: 14, numeric: false },
            {
                header: 'Ingreso Puerto',
                key: 'ingresoPuerto',
                width: 14,
                numeric: false,
            },
            { header: 'Stacking', key: 'stacking', width: 14, numeric: false },
            {
                header: 'Liquidación',
                key: 'liquidacion',
                width: 14,
                numeric: false,
            },
            {
                header: 'Salida Puerto',
                key: 'salidaPuerto',
                width: 14,
                numeric: false,
            },
            {
                header: 'Estadía Puerto',
                key: 'estadiaPuerto',
                width: 13,
                numeric: true,
            },
            {
                header: 'Comentarios',
                key: 'comentarios',
                width: 30,
                numeric: false,
            },
        ];

        columnas.forEach((col, i) => {
            worksheet.getColumn(i + 1).width = col.width;
        });

        // Row 4: Cabecera
        const headerRow = worksheet.getRow(4);
        headerRow.height = 30;
        columnas.forEach((col, i) => {
            const cell = worksheet.getCell(4, i + 1);
            cell.value = col.header;
            cell.font = {
                name: 'Calibri',
                size: 12,
                bold: true,
                color: { argb: 'FFFFFFFF' },
            };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF' + colors.primary },
            };
            cell.alignment = {
                vertical: 'middle',
                horizontal: 'center',
                wrapText: true,
            };
            cell.border = {
                top: { style: 'medium', color: { argb: 'FF' + colors.dark } },
                bottom: {
                    style: 'medium',
                    color: { argb: 'FF' + colors.dark },
                },
                left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
            };
        });

        const fmtFecha = (f: string | null | undefined): string => {
            if (!f) return '-';
            const d = new Date(f);
            if (isNaN(d.getTime())) return '-';
            return d.toLocaleString('es-CL', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'UTC',
            });
        };

        data.forEach((item, index) => {
            const rowNum = 5 + index;
            const row = worksheet.getRow(rowNum);
            row.height = 22;
            const fillColor =
                index % 2 === 0
                    ? { argb: 'FFFFFFFF' }
                    : { argb: 'FF' + colors.light };

            const values: (string | number | null)[] = [
                item.referencia || '-',
                item.cliente || '-',
                item.exportador || '-',
                item.guia || '-',
                item.estadoGuia || '-',
                item.bodega || '-',
                '', // Nivel (no disponible en la vista)
                item.patente || '-',
                item.nave || '-',
                item.booking || '-',
                item.pol || '-',
                fmtFecha(item.etd),
                item.pod || '-',
                item.consignatario || '-',
                item.dus || '-',
                item.contenedor || '-',
                item.cantidadCntrs ?? '-',
                item.tipoEmbarque || '-',
                item.cantidadPallets ?? '-',
                fmtFecha(item.despacho),
                fmtFecha(item.visacion),
                fmtFecha(item.ingresoPuerto),
                fmtFecha(item.stacking),
                fmtFecha(item.liquidacion),
                fmtFecha(item.salidaPuerto),
                item.estadiaPuerto ?? '-',
                item.comentarios || '-',
            ];

            values.forEach((val, colIdx) => {
                const cell = row.getCell(colIdx + 1);
                cell.value = val as ExcelJS.CellValue;
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: fillColor,
                };
                cell.font = {
                    name: 'Calibri',
                    size: 11,
                    color: { argb: 'FF000000' },
                };
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: columnas[colIdx].numeric ? 'right' : 'left',
                };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    bottom: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    left: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    right: { style: 'thin', color: { argb: 'FFd1d5db' } },
                };
                if (columnas[colIdx].numeric && typeof val === 'number') {
                    cell.numFmt = '#,##0';
                }
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ReporteGuias_${new Date()
            .toISOString()
            .replace(/[-:.T]/g, '')
            .slice(0, 14)}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    /**
     * Descarga el Reporte de Guías de Despacho para un DUS específico desde el listado.
     */
    async exportarReporteGuiasPorDus(dus: ControlDusItem): Promise<void> {
        this.isGeneratingReporteGuias = true;
        this.logger.info(
            `📋 Exportando Reporte Guías por DUS: nroIE=${dus.nroIE}`,
            'ControlDusComponent'
        );

        try {
            const data = await firstValueFrom(
                this.controlDusService.getDispatcherReportByNroIE(dus.nroIE)
            );

            if (!data?.length) {
                this.snackBar.open(
                    'No hay guías de despacho para este DUS',
                    'Cerrar',
                    {
                        duration: 3000,
                        panelClass: ['warning-snackbar'],
                    }
                );
                return;
            }

            await this.generarExcelReporteGuias(data);
            this.snackBar.open('Reporte generado correctamente', 'Cerrar', {
                duration: 3000,
                panelClass: ['success-snackbar'],
            });
        } catch (error) {
            this.logger.error(
                'Error al exportar Reporte Guías por DUS',
                error as Error
            );
            this.snackBar.open('Error al generar el reporte', 'Cerrar', {
                duration: 4000,
                panelClass: ['error-snackbar'],
            });
        } finally {
            this.isGeneratingReporteGuias = false;
        }
    }

    verPackingListsDesdeLista(dus: ControlDusItem): void {
        this.logger.info(
            `Abrir Packing Lists en nueva pestaña desde listado: nroIE=${dus.nroIE}, DUS=${dus.dus}`,
            'ControlDusComponent'
        );
        this.actualizarEstadoFiltros();

        const queryParams = { from: 'list' };
        let urlTree;
        if (dus.dus) {
            urlTree = this.router.createUrlTree(
                ['/control-dus', dus.nroIE, 'packing-list', dus.dus],
                { queryParams }
            );
        } else {
            this.logger.warn(
                `El DUS (ref IE: ${dus.nroIE}) no tiene número de DUS asignado. Usando fallback.`
            );
            urlTree = this.router.createUrlTree(
                ['/control-dus', dus.nroIE, 'packing-list', 'SIN-DUS'],
                { queryParams }
            );
        }

        window.open(
            this.router.serializeUrl(urlTree),
            '_blank',
            'noopener,noreferrer'
        );
    }
}
